import {MonnifyUser} from '../../shared/models/loan.model';
import {
	BOPhysicalAddressApiPayload,
	Business,
	BusinessOwner,
	BusinessOwnerStatus,
	BusinessOwnersModel,
	BusinessTypeModel,
	KycNoncompliantCount,
	OwnerBusinessesModel,
	UserActivationValidationModel,
	WaitListFeatureModel,
} from '../../shared/models/business.model';
import {LocalStorageService} from './local-storage.service';
import {StringHelper} from '../helpers/strings.helper';
import {Subject} from 'rxjs';
import {Injectable} from '@angular/core';
import {ApiClientHelper} from '../helpers/api-client.helper';
import {AppConfig} from '../../shared/config/app-config';
import {CustomFileModel} from '../../lean-shared/components/custom-image-uploader/custom-image-uploader.component';
import {Router} from '@angular/router';
import { ApprovalLevel } from '../../shared/models/relation-manager.model';
import {GeocodingResponse} from "../../kamona/model/kamonal-model";
import { StorageKeys } from '../../shared/models/storage-keys';

@Injectable()
export class BusinessService {
	public businesses = new Subject();
	passport: CustomFileModel;
	utilityBill: CustomFileModel;
	identificationFile: CustomFileModel;
	signatureFile: CustomFileModel;
	selectedBusinessOwner: BusinessOwner;
	otpCreationRoutes: OTPCreationRoute[] = [];
	otpFormData = {
		password: '',
		newPin: '',
		pinResetOTP: '',
		currentRoute: null,
	};
	businessCategories: any[] = [];
	private businessOwnerCache: BusinessOwner;
	private business: Business;
	private otpCreationNavigationObserver = new Subject<any>();
	otpCreationNavigationObserver$ = this.otpCreationNavigationObserver.asObservable();
	private businessCategoryObserver = new Subject<any>();
	businessCategoryObserver$ = this.businessCategoryObserver.asObservable();

	constructor(
		private apiClientHelper: ApiClientHelper,
		private localStorage: LocalStorageService,
		public config: AppConfig,
		private router: Router
	) {
	}

  getRouteTracker(){
    return this.localStorage.getRouteTracker();
  }

  routeToUrl(url: string){
    this.router.navigateByUrl(url);
  }

	private _savedBusinessOwner: BusinessOwner;

	get savedBusinessOwner() {
		return this._savedBusinessOwner;
	}

  allowBrmAddBusiness(){
    return this.config.ALLOW_BRM_ADD_BUSINESS;
  }

  allowBrmCreateBusiness(){
    return this.config.ALLOW_SIGN_UP_FOR_BRM;
  }

	set savedBusinessOwner(businessOwner: BusinessOwner) {
		this._savedBusinessOwner = businessOwner;
	}

	pruneImagesFromBusinessOwner(businessOwner) {
		const businessOwnerCache = StringHelper.deepCloneObject(businessOwner);
		if (businessOwnerCache.kyc) {
			businessOwnerCache.kyc.signature = null;
			businessOwnerCache.kyc.utilityBill = null;
			businessOwnerCache.kyc.identificationDocument = null;
			businessOwnerCache.kyc.passport = null;
			if (businessOwnerCache.kyc.entityKYCBvn) {
				businessOwnerCache.kyc.entityKYCBvn.bvnImage = null;
			}
		}
		return businessOwnerCache;
	}

	get businessOwner() : Promise<BusinessOwner>{
		if (this.businessOwnerCache) {
			return Promise.resolve(this.businessOwnerCache);
		}
		const boId = this.localStorage.get(StorageKeys.BUSINESS_OWNER_ID)
		return this.getBusinessOwnerInfo(boId)
	}

	set saveBusinessOwnerId(businessOwner: BusinessOwner) {
		this.businessOwnerCache = businessOwner;
		this.localStorage.save(StorageKeys.BUSINESS_OWNER_ID, businessOwner.id);
	}

	get _business() {
		const owner = this.localStorage.get('business');
		return JSON.parse(owner);
	}

	set _business(business: Business) {
		this.business = business;
		this.localStorage.save('business', JSON.stringify(business));
	}

	canOpenBoDigitalAccount() {
		return this.config.CAN_OPEN_BO_DIGITAL_ACCOUNT;
	}

	showBoTableV2() {
		return this.config.SHOW_BO_TABLE_V2;
	}

	canAddBusinessForPendingBo() {
		return this.config.CAN_ADD_BUSINESSES_FOR_PENDING_BO;
	}

	canBrmExtendGracePeriodAfterExpiration() {
		return this.config.CAN_BRM_EXTEND_GRACE_PERIOD_AFTER_EXPIRATION;
	}

	useRegisterBusinessKycLabel(){
		return this.config.USE_REGISTERED_BUSINESS_PRIMER_LABEL
	}

	useBrmCreateBoBusinessPage() {
		return this.config.USE_BRM_CREATE_BO_BUSINESS_PAGE
	}

	setKycBusinessOwnerDetails(details) {
		if (!details) {
			this.localStorage.deleteValue('KycBusinessOwnerDetails');
			return;
		}
		this.localStorage.save('KycBusinessOwnerDetails', JSON.stringify(details));
	}

	getKycBusinessOwnerDetails() {
		const details = this.localStorage.get('KycBusinessOwnerDetails');
		if (!details) {
			return null;
		}
		return JSON.parse(details);
	}

	setPosRequestSelectedBusiness(business) {
		this.localStorage.save('posRequestSelectedBusiness', JSON.stringify(business));
	}

	getPosRequestSelectedBusiness() {
		const business = this.localStorage.get('posRequestSelectedBusiness');
		if (!business) {
			return null;
		}
		return JSON.parse(business);
	}

	resendBusinessOwnerActivationEmail(id, body): Promise<any> {
		const url = `${this.config.MONIEPOINT_HOST}/api/v2/business-owners/${id}/pending-business-owner/resend-activation`;
		return this.apiClientHelper.putForPromise(url, body);
	}

	resendBusinessOwnerActivationEmailV2(id, body): Promise<any> {
		const url = `${this.config.MONIEPOINT_ONBOARDING}/api/v2/business-owners/${id}/pending-business-owner/resend-activation`;
		return this.apiClientHelper.putForPromise(url, body);
	}

	updatePendingBusinessOwnerContact(id, body): Promise<any> {
		const url = `${this.config.MONIEPOINT_HOST}/api/v2/business-owners/${id}/update-pending-business-owner`;
		return this.apiClientHelper.putForPromise(url, body);
	}

	updatePendingBusinessOwnerContactV2(id, body): Promise<any> {
		const url = `${this.config.MONIEPOINT_ONBOARDING}/api/v2/business-owners/${id}/update-pending-business-owner`;
		return this.apiClientHelper.putForPromise(url, body);
	}

	getBusinessOwners(params, status:BusinessOwnerStatus = "active"): Promise<BusinessOwnersModel> {
		const urlString = StringHelper.toUrlString(params);
		let url = `${this.config.MONIEPOINT_HOST}/api/v2/business-owners/${status}?${urlString}`;
		if(status === 'noncompliant' && this.config.SHOW_BRM_INCOMPLIANT_KYC_BANNER){
			url = `${this.config.MONIEPOINT_HOST}/api/v3/business-owners/${status}?${urlString}`
		}
		return this.apiClientHelper.getForPromise(url);
	}

	getNoncompliantBusinessOwnersCount(): Promise<KycNoncompliantCount> {
		const url = `${this.config.MONIEPOINT_HOST}/api/v3/business-owners/noncompliant-count`;
		return this.apiClientHelper.getForPromise(url);
	}

	getBusinessOwnerDetail(): Promise<BusinessOwnerDetails> {
		const url = `${this.config.MONIEPOINT_DASHBOARD}/api/v2/business-owners/details`;
		return this.apiClientHelper.getForPromise(url);
	}

	getBusinessOwnerBusinesses(id, params): Promise<OwnerBusinessesModel> {
		const urlString = StringHelper.toUrlString(params);
		const url = `${this.config.MONIEPOINT_HOST}/api/v2/business-owners/${id}/businesses?${urlString}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getBusinesses(params): Promise<OwnerBusinessesModel> {
		const urlString = StringHelper.toUrlString(params);
		const url = `${this.config.MONIEPOINT_DASHBOARD}/api/v2/businesses?${urlString}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getStaffUserLinkedBusinesses(): Promise<Business[]> {
		const url = `${this.config.MONIEPOINT_HOST}/api/v1/staff-user/linked-businesses`;
		return this.apiClientHelper.getForPromise(url);
	}

	getStaffUserLinkedBusinessesAndAuthorities(): Promise<Business[]> {
		const url = `${this.config.MONIEPOINT_HOST}/api/v1/staff-user/authorities`;
		return this.apiClientHelper.getForPromise(url);
	}

	getBusinessesV3(params?: any): Promise<OwnerBusinessesModel> {
		const urlString = StringHelper.toUrlString(params);
		const url = `${this.config.MONIEPOINT_HOST}/api/v3/businesses?${urlString}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getBusinessesV3Mobile(params?: any): Promise<Business[]> {
		const urlString = StringHelper.toUrlString(params);
		const url = `${this.config.MONIEPOINT_DASHBOARD}/api/v3/businesses/mobile?${urlString}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getBusinessesOfRM(params): Promise<OwnerBusinessesModel> {
		const urlString = StringHelper.toUrlString(params);
		const url = `${this.config.MONIEPOINT_HOST}/api/v1/agents?${urlString}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getBusinessUsers(ownerId): Promise<MonnifyUser[]> {
		const url = `${this.config.MONIEPOINT_HOST}/api/v2/businesses/${ownerId}/users`;
		return this.apiClientHelper.getForPromise(url);
	}

	getBusinessOwnerLoans(id, params): Promise<OwnerBusinessesModel> {
		const urlString = StringHelper.toUrlString(params);
		const url = `${this.config.MONIEPOINT_HOST}/api/v2/business-owners/${id}/loan-contracts`;
		return this.apiClientHelper.getForPromise(url);
	}

	getWaitListFeatures(): Promise<WaitListFeatureModel[]> {
		const url = `${this.config.MONIEPOINT_HOST}/api/v1/wait-list-features`;
		return this.apiClientHelper.getForPromise(url);
	}


	getBusinessOwnerInfo(id): Promise<BusinessOwner> {
		const url = `${this.config.MONIEPOINT_HOST}/api/v2/business-owners/${id}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getBusinessOwnerExtendGracePeriod(): Promise<any> {
		const url = `${this.config.MONIEPOINT_HOST}/api/v2/business-owners/grace-period-extension`;
		return this.apiClientHelper.getForPromise(url);
	}

	setBusinessOwnerExtendGracePeriod(id, params): Promise<any> {
		const url = `${this.config.MONIEPOINT_HOST}/api/v2/business-owners/extend-grace-period/${id}`;
		return this.apiClientHelper.postForPromise(url, params);
	}

	getBusinessTypes(params): Promise<BusinessTypeModel> {
		const urlString = StringHelper.toUrlString(params);
		const url = `${this.config.MONIEPOINT_HOST}/api/v2/business-types?${urlString}`;
		return this.apiClientHelper.getForPromise(url);
	}

	createBusinessOwner(body) {
		const strippedBody = StringHelper.removeEmptyValues(body);
		const url = `${this.config.MONIEPOINT_HOST}/api/v2/business-owners`;
		return this.apiClientHelper.newPostAsFormDataForPromise(url, strippedBody);
	}

	createBusiness(ownerId, body) {
		const strippedBody = StringHelper.removeEmptyValues(body);
		const url = `${this.config.MONIEPOINT_HOST}/api/v2/business-owners/${ownerId}/businesses`;
		return this.apiClientHelper.newPostAsFormDataForPromise(url, strippedBody);
	}

	onboardWithBusiness(params): Promise<any> {
		const url = `${this.config.MONIEPOINT_HOST}/api/v2/business-owners/onboard-with-business`;
		return this.apiClientHelper.newPostAsFormDataForPromise(url, params);
	}

	onboardWithAlreadyExistingBusinessOwner(params, id): Promise<any> {
		const url = `${this.config.MONIEPOINT_HOST}/api/v2/business-owners/${id}/businesses`;
		return this.apiClientHelper.postForPromise(url, params);
	}

	createBusinessUser(ownerId, body) {
		const strippedBody = StringHelper.removeEmptyValues(body);
		const url = `${this.config.MONIEPOINT_HOST}/api/v2/businesses/${ownerId}/users`;
		return this.apiClientHelper.postForPromise(url, strippedBody);
	}

	createBusinessCategory(body) {
		const strippedBody = StringHelper.removeEmptyValues(body);
		const url = `${this.config.MONIEPOINT_HOST}/api/v2/businesses/categorize-business`;
		return this.apiClientHelper.postForPromise(url, strippedBody);
	}

	activateBusinessUser(body) {
		const url = `${this.config.MONIEPOINT_HOST}/api/v2/users/account/activation`;
		return this.apiClientHelper.postForPromise(url, body, false);
	}

	activateBusinessOwner(body) {
		const url = `${this.config.MONIEPOINT_HOST}/api/v2/business-owners/activate`;
		return this.apiClientHelper.postForPromise(url, body, false);
	}

	sendReactivationLink(body) {
		const url = `${this.config.MONIEPOINT_HOST}/api/v2/business-owners/resend-activation-link`;
		return this.apiClientHelper.postForPromise(url, body, false);
	}

	validateActivationToken(params: { token: string }): Promise<UserActivationValidationModel> {
		const urlString = StringHelper.toUrlString(params);
		const url = `${this.config.MONIEPOINT_HOST}/api/v2/users/account/activation/validate?${urlString}`;
		return this.apiClientHelper.getForPromise(url, true);
	}

	validateBvn(params): Promise<any> {
		const url = `${this.config.MONIEPOINT_HOST}/api/v2/customers/exists/${params}`;
		return this.apiClientHelper.getForPromise(url);
	}

	validateBusinessOwnerBvn(bvn): Promise<any> {
		const url = `${this.config.MONIEPOINT_HOST}/api/v2/business-owners/${bvn}/exists`;
		return this.apiClientHelper.getForPromise(url);
	}

	getBusinessAccounts(id) {
		const url = `${this.config.KUWEGO_LOANS}/accounts/business-owner/${id}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getDisbursementAccounts(id) {
		const url = `${this.config.KUWEGO_LOANS}/accounts/business-owner/${id}/disbursement`;
		return this.apiClientHelper.getForPromise(url);
	}

	getRepaymentAccounts(id) {
		const url = `${this.config.KUWEGO_LOANS}/accounts/business-owner/${id}/repayment`;
		return this.apiClientHelper.getForPromise(url);
	}

	getBusinessCategories(apiVersion = 'v2') {
		const url = `${this.config.MONIEPOINT_HOST}/api/${apiVersion}/business-categories`;
		return this.apiClientHelper.getForPromise(url);
	}

	getBusinessSubCategories(id, apiVersion = 'v2') {
		const url = `${this.config.MONIEPOINT_HOST}/api/${apiVersion}/business-categories/${id}/types`;
		return this.apiClientHelper.getForPromise(url);
	}

	createNewBusiness(payload) {
		const url = `${this.config.MONIEPOINT_DASHBOARD}/api/v2/businesses`;
		return this.apiClientHelper.postForPromise(url, payload, false);
	}

	getUserBusinesses() {
		// /api/v2/businesses
		const url = `${this.config.MONIEPOINT_DASHBOARD}/api/v2/businesses`;
		return this.apiClientHelper.getForPromise(url);
	}

	getBrmCompliance(): Promise<BRMDetailModel> {
		const url = `${this.config.MONIEPOINT_HOST}/api/v2/businesses/brm-compliance`;
		return this.apiClientHelper.getForPromise(url);
	}

	recoverBusinesssOwnerUsername(id) {
		// /api/v2/businesses
		const url = `${this.config.MONIEPOINT_HOST}/api/v2/business-owners/${id}/recover-usernames`;
		return this.apiClientHelper.getForPromise(url);
	}

	validateBusinessOwner(payload): Promise<any> {
		const url = `${this.config.MONIEPOINT_HOST}/api/v3/business-owners/validate/business-owner-onboarding`;
		return this.apiClientHelper.newPutAsFormDataForPromise(url, payload);
	}

	validateBusinessName(name): Promise<any> {
		const url = `${this.config.MONIEPOINT_HOST}/api/v2/businesses/validate-business-name?businessName`;
		const payload = {
			businessName: name,
		};
		return this.apiClientHelper.newPutAsFormDataForPromise(url, payload);
	}

	checkBusinessName(name): Promise<any> {
		const url = `${this.config.MONIEPOINT_HOST}/api/v2/businesses/validate-business-name?businessName=${name}`;
		return this.apiClientHelper.putForPromise(url, {});
	}

	checkBusinessNameDuringOnboarding(name, checkAuth = true): Promise<any> {
		const body = {businessName: name};
		const url = `${this.config.MONIEPOINT_HOST}/api/v1/onboarding/validate-business-name`;
		return this.apiClientHelper.postForPromise(url, body, checkAuth);
	}

	navigateKYCForm(currentRoute = null) {
		if (currentRoute) {
			this.processNavigation(currentRoute);
			return;
		}
		const route = businessPinCreationRoutes.find((x) => !x.completed);
		if (!route) {
			return;
		}
		this.processNavigation(route);
	}

	callNavigationObserver(params) {
		this.otpCreationNavigationObserver.next(params);
	}

	updateKycRoutes(otpCreationRoutes: OTPCreationRoute[]) {
		otpCreationRoutes.forEach((e) => {
			this.updateKycRoute(e);
		});
	}

	updateKycRoute(otpRoute: OTPCreationRoute) {
		const pinCreationRoutes = this.getKycRoutes();
		pinCreationRoutes.forEach((e) => {
			if (e.key === otpRoute.key) {
				e.completed = otpRoute.completed;
			}
		});
		this.otpCreationRoutes = pinCreationRoutes;
	}

	getKycRoutes(): OTPCreationRoute[] {
		const defaultRoute = businessPinCreationRoutes;
		const savedRoutes = defaultRoute;
		return savedRoutes;
	}

	resetRoute() {
		const pinCreationRoutes = this.getKycRoutes();
		pinCreationRoutes.forEach((e) => {
			e.completed = false;
		});
		this.otpCreationRoutes = pinCreationRoutes;
	}

	saveData(value) {
		this.otpFormData = value;
	}

	getData() {
		return this.otpFormData;
	}

	callCategoryChangeObserver(res) {
		this.businessCategoryObserver.next(res);
	}

	getNotificationPreference(): Promise<any> {
		const url = `${this.config.MONIEPOINT_ORCHESTRATOR_SERVICE}/v1/channel`;
		return this.apiClientHelper.getForPromise(url);
	}

	updateNotificationPreference(data): Promise<any> {
		const url = `${this.config.MONIEPOINT_ORCHESTRATOR_SERVICE}/v1/channel`;
		return this.apiClientHelper.postForPromise(url, data);
	}

	getOwnwership(businessId: number): Promise<CardOwnerShipResponse> {
		const url = `${this.config.MONIEPOINT_TERMINAL}/api/v1/cards/businesses/${businessId}/ownership`;
		return this.apiClientHelper.getForPromise(url);
	}

	validateCard(data: any): Promise<any> {
		const url = `${this.config.MONIEPOINT_TERMINAL}/api/v1/cards/validations`;
		return this.apiClientHelper.postForPromise(url, data);
	}

	getCardCharges(cardType: string, terminalType?: string): Promise<CardChargesResponse> {
		const url = `${this.config.MONIEPOINT_TERMINAL}/api/v1/cards/costs/${cardType}?terminalType=${terminalType}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getBRMCardAssignmentStatus(): Promise<{ shouldAssignCard: boolean }> {
		const url = `${this.config.MONIEPOINT_TERMINAL}/api/v1/cards/assignment-status`;
		return this.apiClientHelper.getForPromise(url);
	}

	getCardSkipDetail(businessId: number): Promise<CardSkipDetailResponse> {
		const url = `${this.config.MONIEPOINT_TERMINAL}/api/v1/cards/skip-details/${businessId}`;
		return this.apiClientHelper.getForPromise(url);
	}

	routeToBusinessDetail(businessId: number | string){
		this.router.navigateByUrl(`/main-app/aggregator/business-owners/${businessId}`);
	}

	routeToBusinesOwnersList(){
		this.router.navigateByUrl(`/main-app/aggregator/business-owners/list`);
	}

	routeToBusinessOwnerUpdateKycInfo(businessId: number | string){
		this.router.navigateByUrl(`/main-app/aggregator/business-owners/${businessId}/update-kyc-information`);
	}

	protected processNavigation(currentRoute: OTPCreationRoute) {
		this.router.navigateByUrl(`${currentRoute.route}`);
		this.callNavigationObserver(currentRoute);
	}

	getAllBusinesses(paramsString?: any): Promise<any> {
        const url = `${this.config.MONIEPOINT_HOST}/api/v1/partners-account-creation/workflow${paramsString ? `?${paramsString}` : ''}`;
        return this.apiClientHelper.getForPromise(url);
    }

    approveOrDeclineBusinessCreation(id: string, payload: { action: ApprovalLevel; actionReason: string }): Promise<any> {
        const url = `${this.config.MONIEPOINT_HOST}/api/v1/partners-account-creation/workflow/approval/${id}`;
        return this.apiClientHelper.patchForPromise(url, payload);
    }

	updateBOPhysicalAddress(agentManagerCode: string | number, payload: BOPhysicalAddressApiPayload) {
		const endpoint = `/api/v1/physical-address-verification?agentManagerCode=${agentManagerCode}`;
		const url = `${this.config.MONIEPOINT_ONBOARDING}${endpoint}`;

		return this.apiClientHelper.postForPromise(url, payload);
	}

	getBOGeocoding(address: string): Promise<GeocodingResponse>{
		const url = `${this.config.MONIEPOINT_USER_URL}/api/v1/maps/geocode?address=${address}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getBusinessType(id: number) {
		const url = `${this.config.MONIEPOINT_TERMINAL}/api/v2/agents/category-types/${id}`;
		return this.apiClientHelper.getForPromise(url);
	}

	saveBusinessTypesInformation(payload: SaveBusinessTypesPayload): Promise<SaveBusinessTypesResponse> {
		const url = `${this.config.MONIEPOINT_ONBOARDING}/api/v1/business-info/business-type`;

		return this.apiClientHelper.putForPromise(url, payload);
	}

  getBusinessTypesStatus(moniepointAgentId?: number): Promise<void> {
		const url = `${this.config.MONIEPOINT_DASHBOARD}/api/v1/business-info/business-type-update/eligible`;
    return this.apiClientHelper.getForPromise(url, true, true, false, moniepointAgentId ? {moniepointAgentId} : {});
	}

  saveBusinessTypesV3(payload: SaveBusinessTypesV3Payload): Promise<SaveBusinessTypesV3Response> {
		const url = `${this.config.MONIEPOINT_DASHBOARD}/api/v1/business-info/business-type`;
    return this.apiClientHelper.putForPromise(url, payload);
	}

	getBusinessTCStatus(id: number, type = '') {
		const url = `${this.config.MONIEPOINT_TERMINAL}/api/v1/agents/terms-and-conditions/${id}/pending?type=${type}`;
		return this.apiClientHelper.getForPromise(url);
	}

	sendBusinessTCNotification(agentId: number, id: number) {
		const url = `${this.config.MONIEPOINT_TERMINAL}/api/v1/agents/${agentId}/pending-terms-and-conditions/${id}/notification`;
		return this.apiClientHelper.postForPromise(url, {});
	}
}

export interface BusinessTCDetail {
	id?: number;
	gracePeriodDate?: string;
	assignmentGracePeriod?: string;
	hasPendingTermsAndConditions: boolean;
}


export interface SaveBusinessTypesResponse {
	customerBusinessType?: string;
	businessClassification?: string;
	storeFrontImageReference?: string;
}

export interface SaveBusinessTypesPayload {
	businessId: string;
	storeFrontImageReference?: string;
	businessTypeIds?: Array<number>;
}

export interface SaveBusinessTypesV3Payload {
  businessId: string;
  businessTypeIds: Array<number>;
  storeFrontImageReference: string;
  registeredBusinessDocumentReference?: string;
  invoiceReferences?: Array<string>;
  justificationNote?: string;
}

export interface SaveBusinessTypesV3Response {
  message: string;
}

export interface BusinessCategoryTypeResponse {
	businessId: number;
	category: string;
	description: string;
}

export const businessPinCreationRoutes: OTPCreationRoute[] = [
	{
		key: 'PIN_CREATION',
		route: 'pin/business-pin',
		completed: false,
	},
	{
		key: 'OTP',
		route: 'pin/otp',
		completed: false,
	},
];

// Generated by https://quicktype.io

export interface BRMDetailModel {
	id: number;
	businessName: string;
	code: string;
	state: string;
	bvnImage: string;
	businessPhone: string;
	nubanAccountNumber: string;
	businessEmail: string;
	complianceStatus: string;
}

export interface CardOwnerShipResponse {
	businessOwnerHasCard: boolean;
	businessHasCard: boolean;
}

export interface CardChargesResponse {
	costOfCard: number;
	deliveryFee: number;
	totalCardFee: number;
}

export enum OTPCreationFormKeys {
	PIN_CREATION = 'has_completed_pin_creation',
	OTP = 'has_completed_otp',
}

export enum OTPCreationFormRoute {
	PIN_CREATION = '/pin/business-pin',
	OTP = '/pin/otp',
}

export interface OTPCreationRoute {
	key: string;
	completed?: boolean;
	route?: string;
}

// Generated by https://quicktype.io

export interface BusinessOwnerDetails {
	id?: number;
	name: string;
	displayName: string;
	code?: string;
	email: string;
	phoneNumber?: string;
	noOfBusiness?: number;
	status?: string;
	noOfStaffs?: number;
  kycLevel?: number;
}

export interface CardSkipDetailResponse {
	showCardMenu: boolean;
	cardRequired: boolean;
	canSkip: boolean;
	skipLimit: number;
	skipCount: number;
}
